import React from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { GeneralDialog } from 'sharedComponents';
import { requestStatuses, responseStatuses } from '../../../constants';
import { getRequestStatusText, getResponseStatusText, isRequestStatusRequested } from '../helpers';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import LoadingStatusWrapper from 'sharedComponents/WithLoadingStatus/LoadingStatusWrapper';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';
import { apiCallStatus } from 'helpers/constants';

import './style.scss';
import { sensorLastCommandStatus, dcuGeneration } from '../constants';

const CollectRawDataComponent = ({
    model: {
        loadingStatuses,
        rawDataCollectionRequest,
        rawDataCollectionResponseMessage,
        rawDataCollectionData,
        sensorGatewayInfo,
        errorData,
        collectRawDataErrorData,
    },
    actions: {
        collectRawData
    },
    show,
    close,
    onRetry,

}) => {
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const DcuGenerationProperty = sensorGatewayInfo?.data?.dcuGeneration;
    const isDcuGeneration = DcuGenerationProperty === dcuGeneration.DCUGENERATION_PROPERTY;

    const renderRequestStatusTime = () => {
        if (isDcuGeneration) {
            return isRequestStatusRequested(rawDataCollectionRequest?.featureValue) &&
                <Col>{formatDateTime(rawDataCollectionRequest?.timeStamp)}</Col>;
        }
        return rawDataCollectionData?.commandStatus &&
            <Col>{formatDateTime(rawDataCollectionData?.requestTime)}</Col>;
    };

    const renderErrorReason = () => {
        if (isDcuGeneration) {
            if (rawDataCollectionResponseMessage?.featureValue !== null &&
                rawDataCollectionResponseMessage?.featureValue !== responseStatuses.COMPLETED_SUCCESSFULLY) {
                return <Row className='error-reason-row gx-0'>
                    <Col>{translate('ABB.Powertrain.Frontend.errorReasonLabel')}&nbsp;
                        <span className='error-reason-text'>
                            {getResponseStatusText(rawDataCollectionResponseMessage?.featureValue)}
                        </span>
                    </Col>
                </Row>;
            }
            return null;
        }
        if (rawDataCollectionData?.commandStatus &&
            rawDataCollectionData?.commandStatus !== sensorLastCommandStatus.SUCCESS &&
            rawDataCollectionData?.errorReason) {
            return <Row className='error-reason-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.errorReasonLabel')}&nbsp;
                    <span className='error-reason-text'>
                        {rawDataCollectionData?.errorReason}
                    </span>
                </Col>
            </Row>;
        }
        return null;
    };

    const mainContent = errorData?.loadingStatus === apiCallStatus.ERROR
        ? <ErrorView
            errorCode={errorData.errorCode}
            onRetry={onRetry}
            onReportIssue={showReportIssue}
        />
        : <LoadingStatusWrapper
            useCustomAnchor
            transparent
            loadingStatus={loadingStatuses}>
            <Row className='info-text-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.collectRawDataInfoText')}</Col>
            </Row>
            <Row className='info-text-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.collectRawDataNoteText')}</Col>
            </Row>
            <Row className='request-status-row gx-0'>
                <Col>{translate('ABB.Powertrain.Frontend.requestStatusLabel')}&nbsp;
                    <span className='request-status-text'>
                        {isDcuGeneration ? rawDataCollectionRequest && getRequestStatusText(rawDataCollectionRequest?.featureValue) : rawDataCollectionData?.commandStatus}
                    </span>
                </Col>
                {renderRequestStatusTime()}
            </Row>
            {renderErrorReason()}
        </LoadingStatusWrapper>;

    return <GeneralDialog
        show={show}
        close={close}
        title={translate('ABB.Powertrain.Frontend.collectRawDataDialogTitle')}
        dialogClassName='collect-raw-data-dialog'
        cancelButtonProps={{
            text: translate('ABB.Powertrain.Frontend.cancelLabel'),
        }}
        acceptButtonProps={{
            disabled: isDcuGeneration ? rawDataCollectionRequest?.featureValue === requestStatuses.PENDING : [sensorLastCommandStatus.PENDING, sensorLastCommandStatus.EXECUTING].includes(rawDataCollectionData?.commandStatus),
            text: translate('ABB.Powertrain.Frontend.requestButton'),
            onClick: () => {
                collectRawData().then(close).catch(() => { });
            }
        }}
        closeButton={true}
    >
        <Container fluid className='collect-raw-data-container gx-0'>
            {collectRawDataErrorData?.loadingStatus === apiCallStatus.ERROR
                ? <ErrorView
                    errorCode={collectRawDataErrorData.errorCode}
                    onRetry={() => collectRawData().catch(() => { })}
                    onReportIssue={showReportIssue}
                />
                : mainContent
            }
        </Container>
    </GeneralDialog>;
};

CollectRawDataComponent.propTypes = {
    model: PropTypes.shape({
        loadingStatuses: PropTypes.arrayOf(PropTypes.string),
        rawDataCollectionRequest: PropTypes.shape({
            featureValue: PropTypes.number,
            timeStamp: PropTypes.string
        }),
        rawDataCollectionResponseMessage: PropTypes.shape({
            featureValue: PropTypes.number
        }),
        rawDataCollectionData: PropTypes.object,
        sensorGatewayInfo: PropTypes.object,
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.string
        }),
        collectRawDataErrorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.string
        }),
    }),
    actions: PropTypes.shape({
        collectRawData: PropTypes.func
    }),
    show: PropTypes.bool,
    close: PropTypes.func,
    onRetry: PropTypes.func,
};

export default CollectRawDataComponent;
